import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { IResponseHandlerOptions, useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateMerchDistribution,
  IUpdateMerchDistribution,
} from './types';
import { IDistributionResponse, ITemplatesLightListItem } from '../types';

export const useGetMerchDistributionTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['merch-distribution-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MerchDistributionSectionTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetMerchDistributionTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetMerchDistributionSectionTemplate =  (
  id: string,
  options?: IResponseHandlerOptions<IDistributionResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDistributionResponse, DefaultError>({
    queryKey: ['merch-distribution-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MerchDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetMerchDistributionSectionTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostMerchDistributionTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMerchDistributionTemplate'
  );
  return useMutation<string, DefaultError, ICreateMerchDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MerchDistributionSectionTemplate',
        body: { merchDistributionSectionTemplate: body },
      });
      
      return responseHandler(response, {
        groupName: 'usePostMerchDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutMerchDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutMerchDistributionTemplate'
  );
  return useMutation<string, DefaultError, IUpdateMerchDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MerchDistributionSectionTemplate',
        body: {merchDistributionSectionId: id, merchDistributionSectionTemplate: body},
      });

      return responseHandler(response, {
        groupName: 'usePutMerchDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMerchDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteMerchDistributionTemplate'
  );
  return useMutation<string, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `MerchDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteMerchDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
